import { useState, useEffect } from 'react'
import { ThemeToggle } from './components/ThemeToggle'
import './DebugView.css'

interface RouteDebugInfo {
    public_ip: string
    device_connections: Record<string, boolean>
    cast_screen_connections: Record<string, boolean>
    device_descriptions: Record<string, {
        private_ip: string
        device_type: string
    }>
    cast_screen_descriptions: Record<string, {
        private_ip: string
        screen_type: string
        screen_brand: string
        screen_model: string
        screen_os: string
    }>
}

interface DeviceEntry {
    id: number
    created_at: string
    updated_at: string
    device_type: string
    public_ip: string
    private_ip: string
    screen_ip: string
    timestamp: number
    version: number
}

interface ScreenEntry {
    id: number
    created_at: string
    updated_at: string
    screen_type: string
    screen_brand: string
    screen_model: string
    screen_os: string
    public_ip: string
    private_ip: string
    timestamp: number
    split_screen: boolean
    available: boolean
}

interface DebugResponse {
    route_map_v2: Record<string, RouteDebugInfo>
    room_sessions: Record<string, unknown>
    device_entries: DeviceEntry[]
    screen_entries: ScreenEntry[]
}

interface DebugViewProps {
    accessToken: string
    onLogout: () => void
}

export function DebugView({ accessToken, onLogout }: DebugViewProps) {
    const [data, setData] = useState<DebugResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
    const [resetUid, setResetUid] = useState('')
    const [resetMsg, setResetMsg] = useState<string | null>(null)
    const [resetting, setResetting] = useState(false)

    const fetchDebugData = async () => {
        if (!accessToken) return
        setLoading(true)
        setError(null)
        try {
            const headers = new Headers()
            headers.set('Authorization', `Bearer ${accessToken}`)
            const response = await fetch('/debug', { headers })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const jsonData = await response.json()
            setData(jsonData)
            setLastUpdated(new Date())
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch debug data')
        } finally {
            setLoading(false)
        }
    }

    const resetUser = async () => {
        const uid = resetUid.trim()
        if (!uid) return
        setResetting(true)
        setResetMsg(null)
        try {
            const headers = new Headers()
            headers.set('Authorization', `Bearer ${accessToken}`)
            headers.set('Content-Type', 'application/json')
            const response = await fetch('/admin/reset-user', {
                method: 'POST',
                headers,
                body: JSON.stringify({ uid }),
            })
            const text = await response.text()
            if (!response.ok) throw new Error(text || `HTTP ${response.status}`)
            const r = JSON.parse(text)
            setResetMsg(`✓ Reset ${r.user_id}: removed ${r.games_removed} game(s), ${r.genres_removed} genre(s); onboarding will replay.`)
        } catch (err) {
            setResetMsg(`✗ ${err instanceof Error ? err.message : 'reset failed'}`)
        } finally {
            setResetting(false)
        }
    }

    useEffect(() => {
        fetchDebugData()
        // Auto-refresh every 5 seconds
        const interval = setInterval(fetchDebugData, 5000)
        return () => clearInterval(interval)
    }, [accessToken])

    if (loading && !data) {
        return (
            <div className="debug-container">
                <div className="debug-header">
                    <h1>Debug View</h1>
                </div>
                <div className="debug-content">
                    <div className="loading">Loading debug data...</div>
                </div>
            </div>
        )
    }

    return (
        <div className="debug-container">
            <div className="debug-header">
                <h1>Debug View</h1>
                <div className="debug-controls">
                    <ThemeToggle />
                    <button onClick={fetchDebugData} disabled={loading} className="refresh-button">
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                    {lastUpdated && (
                        <span className="last-updated">
                            Last updated: {lastUpdated.toLocaleTimeString()}
                        </span>
                    )}
                    <button onClick={onLogout} className="logout-button">
                        Logout
                    </button>
                </div>
            </div>
            <div className="debug-content">
                <section className="debug-section">
                    <h2>Reset User (QA)</h2>
                    <p>Clears the user's games + genres and replays onboarding. Runs against this deployment's Supabase environment.</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="User UUID"
                            value={resetUid}
                            onChange={(e) => setResetUid(e.target.value)}
                            style={{ flex: 1, minWidth: 320, padding: 8 }}
                        />
                        <button
                            onClick={resetUser}
                            disabled={resetting || !resetUid.trim()}
                            className="refresh-button"
                        >
                            {resetting ? 'Resetting…' : 'Reset user'}
                        </button>
                    </div>
                    {resetMsg && <div style={{ marginTop: 8 }}>{resetMsg}</div>}
                </section>

                {error && (
                    <div className="error-message">
                        <strong>Error:</strong> {error}
                        <button onClick={fetchDebugData} className="retry-button">Retry</button>
                    </div>
                )}

                {data && (
                    <div className="debug-sections">
                        <section className="debug-section">
                            <h2>Route Map V2 ({data.route_map_v2 ? Object.keys(data.route_map_v2).length : 0} routes)</h2>
                            <pre className="json-pretty">
                                {JSON.stringify(data.route_map_v2 || {}, null, 2)}
                            </pre>
                        </section>

                        <section className="debug-section">
                            <h2>Room Sessions ({data.room_sessions ? Object.keys(data.room_sessions).length : 0} rooms)</h2>
                            <pre className="json-pretty">
                                {JSON.stringify(data.room_sessions || {}, null, 2)}
                            </pre>
                        </section>

                        <section className="debug-section">
                            <h2>Device Entries ({data.device_entries?.length || 0} entries)</h2>
                            <pre className="json-pretty">
                                {JSON.stringify(data.device_entries || [], null, 2)}
                            </pre>
                        </section>

                        <section className="debug-section">
                            <h2>Screen Entries ({data.screen_entries?.length || 0} entries)</h2>
                            <pre className="json-pretty">
                                {JSON.stringify(data.screen_entries || [], null, 2)}
                            </pre>
                        </section>
                    </div>
                )}
            </div>
        </div>
    )
}
